import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { DoxMascot } from "@/components/Logo";

/**
 * DoxHandoffTransition — the "Dox is getting ready…" bridge that plays
 * between the trial modal submitting and the dashboard/onboarding rendering.
 *
 * Purpose:
 * • Kill the ONE dead moment in the entire signup flow.
 * • Reinforce the mental model: Dox and his team are already working — this
 *   visitor didn't just "buy software", they hired an employee who starts
 *   immediately.
 *
 * The card auto-dismisses after ~2.6s of scripted "work" and then invokes
 * `onDone()`. Every line references a specialist so the Dox Team story is
 * front-and-centre from second one.
 */
const STEPS = [
  { label: "Preparing your workspace…",                            after: 0    },
  { label: "My Research Specialist is loading your state rules…",  after: 550  },
  { label: "My Calendar Specialist is watching every deadline…",   after: 1100 },
  { label: "My Document Specialist is ready to review uploads…",   after: 1650 },
  { label: "You're all set.",                                      after: 2200, done: true },
];

const TOTAL_MS = 2600;

export default function DoxHandoffTransition({ name, onDone }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      setTick(elapsed);
      if (elapsed >= TOTAL_MS) {
        clearInterval(id);
        // Small breath so the "You're all set." checkmark can be felt.
        setTimeout(() => onDone && onDone(), 350);
      }
    }, 90);
    return () => clearInterval(id);
  }, [onDone]);

  const currentIndex = STEPS.reduce((idx, s, i) => (tick >= s.after ? i : idx), 0);
  const progressPct = Math.min(100, (tick / TOTAL_MS) * 100);
  const current = STEPS[currentIndex];
  const first = (name || "").split(" ")[0];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-6"
      data-testid="dox-handoff-transition"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />

      <div className="relative w-full max-w-md rounded-3xl glass-strong border border-white/10 overflow-hidden shadow-2xl">
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-dox-orange/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 h-60 w-60 rounded-full bg-dox-magenta/20 blur-3xl pointer-events-none" />

        <div className="relative px-8 pt-10 pb-8 text-center">
          <div className="mx-auto mb-6 relative w-20 h-20 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-dox-orange/25 blur-2xl animate-pulse" />
            <DoxMascot size={76} emotion={current?.done ? "done" : "thinking"} />
          </div>

          <div className="text-[11px] font-bold tracking-[0.24em] uppercase text-dox-orange mb-2">
            {current?.done ? "Ready" : "On it"}
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl leading-tight text-white">
            {current?.done
              ? (first ? `Welcome, ${first}.` : "Welcome aboard.")
              : "Dox is getting ready…"}
          </h2>

          {/* Live status line — swaps between scripted work items */}
          <div className="mt-4 h-8 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.28 }}
                className="text-sm text-white/70 leading-snug flex items-center gap-2"
                data-testid="handoff-status"
              >
                {current?.done && <CheckCircle2 size={15} className="text-emerald-300 shrink-0" />}
                <span>{current?.label}</span>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress bar — gives real motion to the wait */}
          <div className="mt-6 h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-dox-orange via-dox-magenta to-dox-purple transition-[width] duration-100 ease-linear"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Specialist trail — subtly showing the whole team spinning up */}
          <div className="mt-5 flex justify-center gap-1.5" aria-hidden="true">
            {STEPS.slice(0, 4).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  i <= currentIndex - 1 ? "bg-emerald-400" : i === currentIndex ? "bg-dox-orange animate-pulse" : "bg-white/15"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
